import { useEffect, useRef, useState } from "react";
import useInView from "@/hooks/useInView";

/**
 * Fade-up on scroll into view. Small, opinionated, one-shot.
 * Usage: <Reveal delay={0.1}><h2>…</h2></Reveal>
 */
export const Reveal = ({ children, delay = 0, as: Tag = "div", className = "", ...rest }) => {
  const [ref, visible] = useInView();
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "reveal--in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}s` }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/**
 * Animated integer count-up when the element enters the viewport.
 * Preserves prefix/suffix.
 */
export const CountUp = ({ end, duration = 1400, prefix = "", suffix = "", className = "" }) => {
  const [ref, visible] = useInView();
  const [n, setN] = useState(0);
  const rafRef = useRef();

  useEffect(() => {
    if (!visible) return;
    const start = performance.now();
    const from = 0;
    const step = (now) => {
      const p = Math.min(1, (now - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(from + (end - from) * eased));
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [visible, end, duration]);

  return (
    <span ref={ref} className={className} data-testid={`countup-${end}`}>
      {prefix}
      {n.toLocaleString()}
      {suffix}
    </span>
  );
};

export default Reveal;
